import { TrashIcon } from '@radix-ui/react-icons';
import {
  Box,
  Button,
  Flex,
  ScrollArea,
  Spinner,
  Text,
  TextField,
} from '@radix-ui/themes';
import { useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useStore } from 'react-redux';
import { useEffectOnce, useUpdateEffect } from 'react-use';
import { twMerge } from 'tailwind-merge';
import { RootState } from '@/app/appStore';
import { Overlay } from '@/pages/home/ui/overaly';
import {
  addSku,
  attachPointToSku,
  deleteAllSku,
  deleteSku,
  isReadOnly,
  selectIsControlsDisabled,
  setSelectedSku,
} from '@/entities/collection';
import { useVariantsQuery } from '@/shared/api';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';

export function SkuList({ collectionId }: { collectionId: string | null }) {
  const dispatch = useAppDispatch();
  const skuList = useAppSelector((state) =>
    state.collection.skuList.map((variant, index) => ({
      ...variant,
      variantIndex: index,
    }))
  );
  const store = useStore<RootState>();
  const selectedSku = useAppSelector((state) => state.collection.selectedSku);
  const isReadOnlyMode = useAppSelector(isReadOnly);
  const isControlsDisabled = useAppSelector(selectIsControlsDisabled);

  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');

  const { data, isFetching } = useVariantsQuery({
    page: 1,
    limit: 10,
    sku: query,
  });

  useUpdateEffect(() => {
    const collection = store.getState().collection;

    if (data && data.results.length === 0) {
      toast.error('Product variant not found');
      setSearch('');
      setQuery('');
      return;
    }

    if (data && data.results.length === 1) {
      if (!collection.backgroundImage) {
        toast.error('Capture background image');
        setSearch('');
        setQuery('');
        return;
      }

      if (collection.mode === 'single' && collection.skuList.length > 0) {
        toast.error(
          'Adding more than one product to a list is not allowed in Single-SKU Mode.'
        );
        setSearch('');
        setQuery('');
        return;
      }

      if (collection.mode === 'single') {
        dispatch(
          attachPointToSku({
            variantIndex: 0,
            point: null,
          })
        );
      }

      dispatch(addSku(data.results[0]));
      setSearch('');
      setQuery('');
    }
  }, [data]);

  const inputRef = useRef<HTMLInputElement>(null);

  useEffectOnce(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (
        !e.ctrlKey &&
        !e.shiftKey &&
        !e.metaKey &&
        e.key !== ' ' &&
        !Number.isNaN(Number(e.key))
      ) {
        if (document.activeElement instanceof HTMLInputElement) return;
        inputRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  });

  return (
    <div className="relative flex flex-col flex-grow">
      {!collectionId && <Overlay />}

      <Text as="p" className="font-medium mb-1">
        SKUs being collected
      </Text>
      <TextField.Root
        placeholder="Search the SKU"
        ref={inputRef}
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            setQuery(search);
          }
        }}
      >
        <TextField.Slot side="right">
          {isFetching && <Spinner />}
        </TextField.Slot>
      </TextField.Root>

      <Flex justify="between" className="mt-3">
        <Text size="2" weight="medium">
          Items added: {skuList.length}
        </Text>
        {!isReadOnlyMode && (
          <Button
            color="red"
            variant="soft"
            size="1"
            disabled={!skuList.length || isControlsDisabled}
            onClick={() => {
              dispatch(deleteAllSku());
            }}
          >
            Remove all items
          </Button>
        )}
      </Flex>
      <ScrollArea className="my-2 flex-grow max-h-[calc(100vh-462px)]">
        <ul className="flex flex-col gap-2">
          {[...skuList].reverse().map((sku) => (
            <li
              key={sku.variantIndex}
              onClick={() => {
                dispatch(
                  setSelectedSku(selectedSku !== sku.sku ? sku.sku : null)
                );
              }}
              className={twMerge([
                'hover:bg-gray-100 rounded-lg gap-2 flex items-center border-2 p-2 border-transparent hover:border-blue-400',
                selectedSku === sku.sku &&
                  'bg-blue-100 border-blue-400 hover:bg-blue-200',
              ])}
            >
              <img
                className="min-w-10 w-10 h-10 object-contain "
                src={sku.image}
              />
              <div className="flex-grow flex items-center justify-between">
                <Box>
                  <Text
                    size="2"
                    className="text-ellipsis overflow-hidden line-clamp-2"
                  >
                    {sku.display_name}
                  </Text>
                  <Text size="2" weight="bold">
                    {sku.sku}
                  </Text>
                </Box>
                {!isReadOnlyMode && (
                  <div className="flex items-center pr-2">
                    <Button
                      disabled={isControlsDisabled}
                      color="red"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        dispatch(deleteSku({ index: sku.variantIndex }));
                      }}
                    >
                      <TrashIcon />
                    </Button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      </ScrollArea>
    </div>
  );
}
